import { createContext, forwardRef, useContext, useId } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import "./Radio.css";

interface RadioGroupContextValue {
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "type"> {
  label?: ReactNode;
  invalid?: boolean;
  value: string;
}

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(" ");

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { label, invalid, value, name, checked, onChange, disabled, className, id, ...rest },
  ref,
) {
  const group = useContext(RadioGroupContext);
  const resolvedName = name ?? group?.name;
  const resolvedChecked = group ? group.value === value : checked;
  const resolvedDisabled = disabled ?? group?.disabled;
  const resolvedInvalid = invalid ?? group?.invalid ?? false;

  return (
    <label
      className={cx(
        "go4-radio",
        resolvedInvalid && "go4-radio--invalid",
        resolvedDisabled && "go4-radio--disabled",
        className,
      )}
    >
      <input
        ref={ref}
        id={id}
        type="radio"
        name={resolvedName}
        value={value}
        checked={resolvedChecked}
        disabled={resolvedDisabled}
        aria-invalid={resolvedInvalid || undefined}
        onChange={(event) => {
          onChange?.(event);
          if (group?.onChange && event.target.checked) group.onChange(value);
        }}
        className="go4-radio__input"
        {...rest}
      />
      <span className="go4-radio__circle" aria-hidden="true" />
      {label ? <span className="go4-radio__label">{label}</span> : null}
    </label>
  );
});

export interface RadioGroupProps {
  legend?: ReactNode;
  name?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  orientation?: "vertical" | "horizontal";
  className?: string;
  children?: ReactNode;
}

export function RadioGroup({
  legend,
  name,
  value,
  onChange,
  disabled,
  invalid,
  orientation = "vertical",
  className,
  children,
}: RadioGroupProps) {
  const autoName = useId();
  const ctx: RadioGroupContextValue = {
    name: name ?? `go4-radio-${autoName}`,
    value,
    onChange,
    disabled,
    invalid,
  };
  return (
    <fieldset
      className={cx(
        "go4-radio-group",
        orientation === "horizontal" && "go4-radio-group--horizontal",
        className,
      )}
      aria-invalid={invalid || undefined}
      disabled={disabled}
    >
      {legend ? <legend className="go4-radio-group__legend">{legend}</legend> : null}
      <RadioGroupContext.Provider value={ctx}>{children}</RadioGroupContext.Provider>
    </fieldset>
  );
}
